import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { FileText, Download, Search, ChevronRight, Filter, Calendar, HardDrive, Globe, Package } from 'lucide-react';
import axios from 'axios';
import SEO, { generateBreadcrumbSchema } from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LibraryPage = () => {
  const { t, language } = useLanguage();
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedFileType, setSelectedFileType] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [docsRes, catsRes] = await Promise.all([
          axios.get(`${API}/library`),
          axios.get(`${API}/library/categories`)
        ]);
        setDocuments(docsRes.data);
        setCategories(catsRes.data);
      } catch (error) {
        console.error('Error fetching library:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Extract unique file types
  const fileTypes = [...new Set(documents.map(d => d.file_type))].filter(Boolean);

  // Filter documents
  const filteredDocuments = documents.filter(doc => {
    if (selectedCategory && doc.category !== selectedCategory) return false;
    if (selectedFileType && doc.file_type !== selectedFileType) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const title = language === 'sv' ? doc.title_sv : doc.title_en;
      const desc = language === 'sv' ? doc.description_sv : doc.description_en;
      if (!title?.toLowerCase().includes(query) && !desc?.toLowerCase().includes(query)) {
        return false;
      }
    }
    return true;
  });

  const clearFilters = () => {
    setSelectedCategory('');
    setSelectedFileType('');
    setSearchQuery('');
  };

  const hasActiveFilters = selectedCategory || selectedFileType || searchQuery;

  return (
    <div data-testid="library-page" className="bg-[#FAFAF8] min-h-screen pt-24">
      <SEO 
        title={language === 'sv' 
          ? 'Bibliotek | Tekniska resurser och nedladdningar' 
          : 'Library | Technical Resources and Downloads'}
        description={language === 'sv'
          ? 'Ladda ner produktkataloger, tekniska manualer, mjukvara och certifikat från Flowingo AB. Kompletta tekniska resurser för VVS-proffs.'
          : 'Download product catalogs, technical manuals, software and certificates from Flowingo AB. Complete technical resources for HVAC professionals.'}
        language={language}
        schema={[
          generateBreadcrumbSchema([
            { name: language === 'sv' ? 'Hem' : 'Home', url: 'https://flowingo.se' },
            { name: language === 'sv' ? 'Bibliotek' : 'Library', url: 'https://flowingo.se/bibliotek' },
          ]),
        ]}
      />
      
      {/* Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <nav className="flex items-center gap-2 text-sm text-stone-500 mb-4">
          <Link to="/" className="hover:text-stone-900">{t('nav.home')}</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-stone-900">{t('nav.library')}</span>
        </nav>
        
        <p className="text-stone-500 text-sm uppercase tracking-widest mb-2">
          {t('library.subtitle')}
        </p>
        <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-4">
          {t('library.title')}
        </h1>
        <p className="text-stone-600 max-w-2xl">
          {t('library.description')}
        </p>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        {/* Search and Filters */}
        <div className="mb-8 space-y-4">
          {/* Search bar */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-stone-400" />
            <Input
              type="text"
              placeholder={t('library.search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 pr-4 py-6 text-base bg-white border-stone-300 rounded-none"
            />
          </div>

          {/* Filter pills */}
          <div className="flex flex-wrap gap-3">
            {/* Category filter */}
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2 border border-stone-300 bg-white text-sm text-stone-700 focus:outline-none focus:ring-2 focus:ring-[#321a27]"
            >
              <option value="">{t('library.allCategories')}</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>

            {/* File type filter */}
            <select
              value={selectedFileType}
              onChange={(e) => setSelectedFileType(e.target.value)}
              className="px-4 py-2 border border-stone-300 bg-white text-sm text-stone-700 focus:outline-none focus:ring-2 focus:ring-[#321a27]"
            >
              <option value="">{t('library.allTypes')}</option>
              {fileTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>

            {hasActiveFilters && (
              <Button
                variant="ghost"
                onClick={clearFilters}
                className="text-stone-600 hover:text-stone-900"
              >
                Rensa filter
              </Button>
            )}
          </div>
        </div>

        {/* Results count */}
        <div className="mb-6 text-stone-600 text-sm">
          {filteredDocuments.length} {language === 'sv' ? 'dokument' : 'documents'}
        </div>

        {/* Documents Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <div key={n} className="bg-white h-64 animate-pulse border border-stone-200" />
            ))}
          </div>
        ) : filteredDocuments.length === 0 ? (
          <div className="text-center py-20 bg-white border border-stone-200">
            <FileText className="w-16 h-16 text-stone-300 mx-auto mb-4" />
            <p className="text-stone-600">{t('library.noDocuments')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredDocuments.map((doc) => (
              <DocumentCard key={doc.id} doc={doc} language={language} t={t} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Document Card Component - Zeekr-inspired clean design
const DocumentCard = ({ doc, language, t }) => {
  const title = language === 'sv' ? doc.title_sv : doc.title_en;
  const description = language === 'sv' ? doc.description_sv : doc.description_en;
  const fullUrl = doc.file_url.startsWith('/') 
    ? `${process.env.REACT_APP_BACKEND_URL}${doc.file_url}` 
    : doc.file_url;

  // Get icon based on file type
  const getFileIcon = (type) => {
    switch(type?.toUpperCase()) {
      case 'PDF': return <FileText className="w-8 h-8 text-red-600" />;
      case 'ZIP': case 'RAR': return <Package className="w-8 h-8 text-amber-600" />;
      case 'EXE': case 'MSI': return <HardDrive className="w-8 h-8 text-blue-600" />;
      default: return <FileText className="w-8 h-8 text-stone-600" />;
    }
  };

  return (
    <div className="bg-white border border-stone-200 hover:border-stone-300 transition-all group">
      {/* Header with icon and category */}
      <div className="p-6 border-b border-stone-100">
        <div className="flex items-start justify-between mb-4">
          <div className="w-16 h-16 bg-stone-50 flex items-center justify-center">
            {getFileIcon(doc.file_type)}
          </div>
          <span className="text-xs uppercase tracking-wider text-stone-500 bg-stone-100 px-2 py-1">
            {doc.category}
          </span>
        </div>
        
        <h3 className="text-lg font-semibold text-stone-900 mb-2 line-clamp-2">
          {title}
        </h3>
        
        {description && (
          <p className="text-sm text-stone-600 line-clamp-2">
            {description}
          </p>
        )}
      </div>

      {/* Metadata */}
      <div className="p-6 space-y-3">
        {doc.version && (
          <div className="flex items-center gap-2 text-sm text-stone-600">
            <Package className="w-4 h-4 text-stone-400" />
            <span className="text-stone-500">{t('library.version')}:</span>
            <span className="font-medium">{doc.version}</span>
          </div>
        )}
        
        {doc.file_size && (
          <div className="flex items-center gap-2 text-sm text-stone-600">
            <HardDrive className="w-4 h-4 text-stone-400" />
            <span className="text-stone-500">{t('library.fileSize')}:</span>
            <span className="font-medium">{doc.file_size}</span>
          </div>
        )}
        
        {doc.language && (
          <div className="flex items-center gap-2 text-sm text-stone-600">
            <Globe className="w-4 h-4 text-stone-400" />
            <span className="text-stone-500">{t('library.language')}:</span>
            <span className="font-medium">{doc.language}</span>
          </div>
        )}
        
        {doc.compatibility && (
          <div className="flex items-center gap-2 text-sm text-stone-600">
            <Package className="w-4 h-4 text-stone-400" />
            <span className="text-stone-500">{t('library.compatibility')}:</span>
            <span className="font-medium text-xs">{doc.compatibility}</span>
          </div>
        )}

        {doc.created_at && (
          <div className="flex items-center gap-2 text-sm text-stone-500 pt-2 border-t border-stone-100">
            <Calendar className="w-4 h-4 text-stone-400" />
            {new Date(doc.created_at).toLocaleDateString(language === 'sv' ? 'sv-SE' : 'en-US')}
          </div>
        )}
      </div>

      {/* Download button */}
      <div className="p-6 pt-0">
        <a 
          href={fullUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Button 
            className="w-full bg-[#321a27] hover:bg-[#4a2d3d] text-white rounded-none flex items-center justify-center gap-2"
          >
            <Download className="w-4 h-4" />
            {t('library.download')} ({doc.file_type})
          </Button>
        </a>
      </div>
    </div>
  );
};

export default LibraryPage;
